import { DONE_STATUS } from '../data/types'
import { ArrowLeft, Briefcase, CheckCircle2, Circle, Percent } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Avatar } from '../components/Avatar'
import { BentoCard } from '../components/BentoCard'
import { EmptyState } from '../components/EmptyState'
import { FilterSelect } from '../components/FilterBar'
import { PriorityBadge } from '../components/PriorityBadge'
import { PersonClients } from './team/PersonClients'
import { StatCard } from '../components/StatCard'
import { StatusBadge } from '../components/StatusBadge'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { hasFullVisibility } from '../hooks/useScopedWork'
import { useTickDone } from '../hooks/useTickDone'
import { CheckSquare, ClipboardList, Gauge } from 'lucide-react'
import { daysUntil } from '../lib/format'

export default function TeamDetail() {
  const { id } = useParams()
  const { team, tasks, projects, clients, activityLog } = useAppData()
  const tick = useTickDone()
  const { currentUser } = useAuth()
  const [statusFilter, setStatusFilter] = useState('all')
  const canViewAnyone = hasFullVisibility(currentUser?.role)
  const member = !canViewAnyone && currentUser?.linkedPersonaId !== id ? undefined : team.find((m) => m.id === id)

  const memberTasks = useMemo(() => tasks.filter((t) => t.assigneeId === id), [tasks, id])
  const filteredTasks = useMemo(
    () => (statusFilter === 'all' ? memberTasks : memberTasks.filter((t) => t.status === statusFilter)),
    [memberTasks, statusFilter],
  )
  /*
   * Their open work per client. The clients they are on come from the
   * assignments, not from this: counted off work alone, somebody just put on
   * a client showed nothing for it until their first piece.
   */
  const openByClient = useMemo(() => {
    const counts = new Map<string, number>()
    for (const t of memberTasks) {
      if (t.status === DONE_STATUS) continue
      counts.set(t.clientId, (counts.get(t.clientId) ?? 0) + 1)
    }
    return counts
  }, [memberTasks])
  const activity = activityLog.filter((a) => a.actorId === id)

  if (!member) {
    return <EmptyState title="Team member not found" description="This person may have been removed." />
  }

  const upcoming = [...memberTasks].filter((t) => t.status !== DONE_STATUS).sort((a, b) => daysUntil(a.dueDate) - daysUntil(b.dueDate))

  return (
    <div className="flex flex-col gap-5">
      <Link to="/people" className="inline-flex items-center gap-1.5 text-sm font-medium text-canvas-fg/60 hover:text-canvas-fg">
<ArrowLeft size={15} /> Back to team
      </Link>

      <div className="flex flex-col gap-4 rounded-card border border-shell-border bg-shell p-6 shadow-card sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Avatar name={member.name} color={member.color} size="lg" />
          <div>
            <h1 className="font-display text-xl font-bold text-shell-text-active">{member.name}</h1>
            <p className="text-sm text-shell-text">{member.title}</p>
            {/*
              * An email and a joining date were here, and the roster holds
              * neither — so both rendered as an icon with nothing after it,
              * which reads as a page that failed to load rather than a fact
              * nobody has recorded. What is known goes here; the rest comes
              * back when there is somewhere to keep it.
              */}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-shell-text">
<span className="flex items-center gap-1"><Briefcase size={12} /> {member.department}</span>
            </div>
          </div>
        </div>
        <StatusBadge status={member.status} />
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Open" value={member.activeTasks} icon={ClipboardList} accent="#3b7dde" index={0} />
        <StatCard label="Went out" value={member.completedTasks} icon={CheckSquare} accent="#2f9e57" index={1} />
        <StatCard label="Of capacity" value={member.workload} suffix="%" icon={Gauge} accent="#d98e1c" index={2} />
        {/*
          * "Performance", with a rising-trend icon, for a ratio that falls
          * when somebody is given more to do and that nothing here compares
          * against last month. It is the share of their pieces that have gone
          * out, and it is labelled that.
          */}
        <StatCard label="Share posted" value={member.performance} suffix="%" icon={Percent} accent="#FC2265" index={3} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PersonClients personId={member.id} name={member.name} openByClient={openByClient} />
        </div>

        <BentoCard title="Activity">
          <div className="flex flex-col gap-3.5">
            {activity.length === 0 && <p className="text-sm text-canvas-fg/50">No recent activity.</p>}
            {activity.map((a) => (
              <div key={a.id}>
                <p className="text-sm text-canvas-fg/70">
                  {a.action} <span className="font-medium text-canvas-fg">{a.target}</span>
                </p>
                <p className="mt-0.5 text-xs text-canvas-fg/50">{a.time}</p>
              </div>
            ))}
          </div>
        </BentoCard>
      </div>

      <BentoCard
        title="Pieces"
        description="Everything with their name on it"
        action={
          <FilterSelect
            label="Filter by status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: 'All statuses', value: 'all' },
              { label: 'Briefed', value: 'Briefed' },
              { label: 'Editing', value: 'Editing' },
              { label: 'Shot', value: 'Shot' },
              { label: 'Review', value: 'Review' },
              { label: DONE_STATUS, value: DONE_STATUS },
            ]}
          />
        }
      >
        <div className="divide-y divide-canvas-fg/10">
          {filteredTasks.map((t) => {
            const client = clients.find((c) => c.id === t.clientId)
            const project = projects.find((p) => p.id === t.projectId)
            return (
              <div key={t.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <button onClick={() => tick(t)} className="text-canvas-fg/30 hover:text-success-500">
                  {t.status === DONE_STATUS ? <CheckCircle2 size={16} className="text-success-500" /> : <Circle size={16} />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-sm font-medium ${t.status === DONE_STATUS ? 'text-canvas-fg/40 line-through' : 'text-canvas-fg'}`}>{t.title}</p>
                  <p className="truncate text-xs text-canvas-fg/50">{client?.name} &middot; {project?.name}</p>
</div>
                <PriorityBadge priority={t.priority} />
                <StatusBadge status={t.status} />
              </div>
            )
          })}
          {filteredTasks.length === 0 && <p className="py-6 text-center text-sm text-canvas-fg/50">No tasks match this filter.</p>}
        </div>
      </BentoCard>

      <BentoCard title="Deadlines">
        <div className="flex flex-col gap-2.5">
          {upcoming.slice(0, 5).map((t) => {
            const due = daysUntil(t.dueDate)
            return (
              <div key={t.id} className="flex items-center justify-between rounded-xl border border-canvas-fg/10 px-3.5 py-2.5">
                <p className="text-sm font-medium text-canvas-fg/90">{t.title}</p>
                <span className={`text-xs font-semibold ${due < 0 ? 'text-danger-500' : due <= 2 ? 'text-warning-500' : 'text-canvas-fg/50'}`}>
                  {due < 0 ? `${Math.abs(due)}d overdue` : due === 0 ? 'Today' : `${due}d left`}
                </span>
              </div>
            )
          })}
          {upcoming.length === 0 && <p className="text-sm text-canvas-fg/50">All caught up — no upcoming deadlines.</p>}
        </div>
      </BentoCard>
    </div>
  )
}
